import EditIcon from "@mui/icons-material/Edit";
import PublicIcon from "@mui/icons-material/Public";
import TargetIcon from "@mui/icons-material/TrackChanges";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Box, Chip, Paper, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemLinearMeter } from "../../../components/UIToolkit/DataViz/SystemLinearMeter/SystemLinearMeter";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { LETTER_SPACING, SURFACE } from "../../../theme/tokens";
import type { PbjCategoryScore } from "../../../utils/models";
import { brandBorder, brandTint, ROW_OPEN_BG, TRACK_BG } from "./colors";
import { ThresholdLadderTable } from "./ThresholdLadderTable";

// Improvement maths for a category's "next goal" card — how far to the next sub-band in
// the metric's natural unit, plus the hours-per-week translation for HPPD categories.
// Computed by the parent row (needs the resolved census) and rendered here verbatim.
export type CategoryImprovement = {
	diff: number;
	pointsGain: number;
	hoursNeeded: number | null;
	nextPoints: number;
	target: number;
};

type Props = {
	category: PbjCategoryScore;
	// Turnover/Admin measures support manual entry — surfaces the "Edit" affordance.
	editable: boolean;
	// N/A (excluded) measures hide the points progress, next-goal, and formula blocks.
	naExcluded: boolean;
	// Available-but-no-points-scale measures render neither the meter nor the progress bar.
	hasScoreScale: boolean;
	// The immediate next sub-band diff + point gain, or null when there's nothing to improve toward.
	improvement: CategoryImprovement | null;
	// The department's safety-margin band buffer (0–100) — passed through to the threshold
	// ladder, which explains a score the CMS cutoffs alone don't account for.
	bandRangeBufferPct: number;
	onEdit: (category: PbjCategoryScore) => void;
};

/**
 * The expanded ("Why this score") body of a breakdown row: current-performance header +
 * value/rating chips, a points-progress meter, the "next goal" action card, the flat
 * threshold ladder, and the formula (turnover fraction or manual-override note). Pure
 * presentation of the passed `category` + derived `improvement` — no local state.
 */
export const CategoryExpandContent: FC<Props> = ({ category, editable, naExcluded, hasScoreScale, improvement, bandRangeBufferPct, onEdit }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const accent = theme.palette.primary.main;
	const tint = brandTint(theme);
	const border = brandBorder(theme);
	// Where a turnover value came from (older payloads omit `source` → "internal"). The CASPER
	// affordances only show while the measure still carries a usable value — an N/A-excluded or
	// force-scored-Invalid measure has no value for the source to explain.
	const isCasper = (category.source ?? "internal") === "casper" && !naExcluded && !(category.invalid ?? false);

	return (
		<Box
			sx={{
				py: 2,
				px: 3,
				bgcolor: ROW_OPEN_BG,
				borderTop: "1px solid",
				borderTopColor: "divider",
				borderBottom: "1px solid",
				borderBottomColor: "divider",
				display: "grid",
				gridTemplateColumns: { xs: "1fr", md: "5fr 7fr" },
				gap: 3,
			}}
		>
			<Stack spacing={3}>
				<Box>
					<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", mb: 1 }}>
						<Typography
							variant="caption"
							sx={{ fontWeight: "bold", textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, display: "block" }}
							color="text.secondary"
						>
							{t("pbj_model.scoring.current_performance")}
						</Typography>
						{editable && (
							<SystemButton
								buttonVariant="borderless"
								size="small"
								leadingIcon={<EditIcon sx={{ fontSize: 14 }} />}
								onClick={(e) => {
									e.stopPropagation();
									onEdit(category);
								}}
								noFullWidthMobile
								data-testid={`pbj-model-category-${category.id}-edit`}
							>
								{t("pbj_model.scoring.manual.edit")}
							</SystemButton>
						)}
					</Stack>
					<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
						<Typography variant="h4" sx={{ fontWeight: "bold" }}>
							{naExcluded ? t("pbj_model.scoring.na.points") : category.value}
						</Typography>
						{naExcluded ? (
							<Chip label={t("pbj_model.scoring.na.chip")} size="small" sx={{ bgcolor: "action.hover", color: "text.secondary", fontWeight: 700 }} />
						) : (
							<Chip
								label={t("pbj_model.scoring.star_rating", { count: category.currentBandStar })}
								size="small"
								sx={{ bgcolor: tint, color: accent, fontWeight: 700 }}
							/>
						)}
						{category.manualOverride && !naExcluded && (
							<Chip label={t("pbj_model.scoring.manual.badge")} size="small" sx={{ bgcolor: tint, color: accent, fontWeight: 700 }} />
						)}
						{isCasper && <Chip label={t("pbj_model.scoring.casper.badge")} size="small" sx={{ bgcolor: tint, color: accent, fontWeight: 700 }} />}
					</Stack>
					{naExcluded && (
						<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
							{t("pbj_model.scoring.na.excluded_note")}
						</Typography>
					)}
				</Box>
				{!naExcluded && hasScoreScale && (
					<Box>
						<Stack direction="row" sx={{ justifyContent: "space-between", mb: 1 }}>
							<Typography variant="caption" sx={{ fontWeight: "bold" }}>
								{t("pbj_model.scoring.points_progress")}
							</Typography>
							<Typography variant="caption" sx={{ fontWeight: "bold" }}>
								{category.score} / {category.maxScore}
							</Typography>
						</Stack>
						{/* Points-progress bar — SystemLinearMeter (progressbar semantics), matching the
						    row's inline meter component instead of a hand-rolled LinearProgress. */}
						<SystemLinearMeter
							value={category.score}
							max={category.maxScore}
							color={accent}
							trackColor={TRACK_BG}
							ariaLabel={t("pbj_model.prediction.meter.aria_label", { title: category.title, score: category.score, max: category.maxScore })}
							dataTestId={`pbj-model-category-${category.id}-progress`}
						/>
					</Box>
				)}
				{improvement && (
					<Paper elevation={0} sx={{ p: 2, bgcolor: SURFACE, border: "1px solid", borderColor: border, borderRadius: 2 }}>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1.5 }}>
							<TargetIcon sx={{ fontSize: 18, color: accent }} />
							<Typography variant="subtitle2" sx={{ fontWeight: "bold", color: accent }}>
								{t("pbj_model.scoring.next_goal", { count: improvement.nextPoints })}
							</Typography>
						</Stack>
						<Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
							{category.kind === "hppd" &&
								t("pbj_model.scoring.action.increase_hppd", {
									title: category.title,
									// Ceil-round to 3 decimals: any positive diff renders as at least 0.001, so we
									// never tell the user "increase by 0.000" when sub-millihppd gaps exist (e.g.
									// raw 0.27499 vs. target 0.275). A diff of exactly 0 still renders 0.000.
									diff: (Math.ceil(improvement.diff * 1000) / 1000).toFixed(3),
								})}
							{category.kind === "turnover" && t("pbj_model.scoring.action.decrease_pct", { title: category.title, diff: improvement.diff.toFixed(2) })}
							{category.kind === "admin" && t("pbj_model.scoring.action.reduce_count", { title: category.title, target: Math.floor(improvement.target) })}
						</Typography>
						<Stack direction="row" spacing={1} sx={{ alignItems: "center", p: 1, bgcolor: tint, borderRadius: 1 }}>
							<TrendingUpIcon sx={{ fontSize: 16, color: accent }} />
							<Typography variant="caption" sx={{ fontWeight: "bold", color: accent }}>
								{improvement.hoursNeeded
									? t("pbj_model.scoring.action.hours_needed", { count: improvement.hoursNeeded })
									: t("pbj_model.scoring.action.points_gain", { count: improvement.pointsGain })}
							</Typography>
						</Stack>
					</Paper>
				)}
			</Stack>
			<Stack spacing={3}>
				<ThresholdLadderTable category={category} bandRangeBufferPct={bandRangeBufferPct} naExcluded={naExcluded} />
				{/* N/A measures hide the formula entirely; a manual override replaces the
				    computed numerator/denominator with a "Manually entered" note. */}
				{!naExcluded &&
					(category.turnoverDetails ? (
						<Box>
							<Typography
								variant="caption"
								sx={{ fontWeight: "bold", textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, mb: 1.5, display: "block" }}
								color="text.secondary"
							>
								{t("pbj_model.scoring.formula_title")}
							</Typography>
							<Stack
								direction="row"
								spacing={3}
								sx={{
									alignItems: "center",
									justifyContent: "center",
									p: 2,
									bgcolor: SURFACE,
									borderRadius: 2,
									border: "1px solid",
									borderColor: "divider",
								}}
							>
								<Box sx={{ textAlign: "center" }}>
									<Typography variant="body2" sx={{ fontWeight: "bold", borderBottom: "2px solid", borderBottomColor: "divider", px: 2, pb: 0.5 }}>
										{category.turnoverDetails.numerator}
									</Typography>
									<Typography variant="caption" color="text.secondary" sx={{ pt: 0.5, display: "block" }}>
										{category.turnoverDetails.denominator}
									</Typography>
								</Box>
								<Typography variant="h5" color="text.secondary">
									=
								</Typography>
								<Box sx={{ textAlign: "center" }}>
									<Typography variant="h5" sx={{ fontWeight: "bold", color: accent }}>
										{category.value}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{t("pbj_model.scoring.formula.final_score")}
									</Typography>
								</Box>
							</Stack>
							{/* Mid-quarter projection (DAT-668): part of the numerator above is people who have
							    been flagged as inactive but whose turnover window has not finished elapsing, so
							    the figure would otherwise look unexplainably higher than the completed departures. */}
							{category.turnoverDetails.pending > 0 && (
								<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
									{t("pbj_model.scoring.formula.pending_note", { count: category.turnoverDetails.pending })}
								</Typography>
							)}
						</Box>
					) : category.manualOverride ? (
						<Box>
							<Typography
								variant="caption"
								sx={{ fontWeight: "bold", textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, mb: 1.5, display: "block" }}
								color="text.secondary"
							>
								{t("pbj_model.scoring.formula_title")}
							</Typography>
							<Stack
								direction="row"
								spacing={1.5}
								sx={{ alignItems: "center", p: 2, bgcolor: tint, borderRadius: 2, border: "1px solid", borderColor: border }}
							>
								<EditIcon sx={{ fontSize: 18, color: accent }} />
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 700, color: accent }}>
										{t("pbj_model.scoring.manual.badge")}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{t("pbj_model.scoring.manual.formula_note", { value: category.value })}
									</Typography>
								</Box>
							</Stack>
						</Box>
					) : isCasper ? (
						// CASPER-sourced value: like a manual override, no numerator/denominator arrives from
						// the backend, so mirror the manual note box with the CASPER copy + icon.
						<Box>
							<Typography
								variant="caption"
								sx={{ fontWeight: "bold", textTransform: "uppercase", letterSpacing: LETTER_SPACING.LABEL, mb: 1.5, display: "block" }}
								color="text.secondary"
							>
								{t("pbj_model.scoring.formula_title")}
							</Typography>
							<Stack
								direction="row"
								spacing={1.5}
								sx={{ alignItems: "center", p: 2, bgcolor: tint, borderRadius: 2, border: "1px solid", borderColor: border }}
							>
								<PublicIcon sx={{ fontSize: 18, color: accent }} />
								<Box>
									<Typography variant="body2" sx={{ fontWeight: 700, color: accent }}>
										{t("pbj_model.scoring.casper.badge")}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{t("pbj_model.scoring.casper.formula_note", { value: category.value })}
									</Typography>
								</Box>
							</Stack>
						</Box>
					) : null)}
			</Stack>
		</Box>
	);
};
